import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import QcAssessmentModal from '../../../../../components/qc-assessment-modal/QcAssessmentModal';
import { getQcTaskTableColumns } from './table/qcTaskTableColumns';

/** Default status tabs (MT-QC-TASK-001). */
const DEFAULT_TOOLBAR_TAB_ITEMS = [
  { value: 'NEW', label: 'NEW' },
  { value: 'SAVE_FOR_LATER', label: 'Saved' },
  { value: 'SUBMITTED', label: 'SUBMITTED' },
];

const QC_TASK_FILTER_PARAM_KEYS = [
  'language',
  'district',
  'state',
  'duration',
  'file_name',
  'assigned_to',
  'assigned_reviewer',
];

/**
 * Server-paginated Material React Table for QC assignments (MT-QC-TASK-001).
 *
 * Performance notes:
 * - Wrapped in `memo` so identical props skip re-render when the parent updates unrelated state.
 * - Table options are `useMemo`'d so `useMaterialReactTable` does not receive a brand-new options
 *   object on every parent render (reduces internal work).
 * - `memoMode: 'cells'` keeps row updates scoped to changed cells where MRT allows it.
 * - Row virtualization helps when many rows are mounted (client slice still bounded by page size).
 *
 * Mobile (xs / sm / below `md`):
 * - Column filters use **popover** mode so the table is not buried under a filter row.
 * - Top toolbar and table grid are **horizontally scrollable** (touch-friendly) so headers/icons fit.
 * - Compact typography, cell padding, toolbars, and tabs to maximize data on screen.
 * - Table body height uses `dvh`; slightly reduced max height vs tablet for tighter layout.
 * - Pagination hides first/last on very narrow widths to reduce horizontal crowding.
 *
 * Stacking: do **not** set a huge z-index on the table Paper in normal mode — it would paint
 * above the mobile sidebar (`z-50`). Only fullscreen uses a high z-index (above App header).
 */
function QcTaskTableComponent({
  data,
  pageWorkflowId,
  pageSizeOptions,
  defaultPageSize,
  activeTab,
  onTabChange,
  pagination,
  onPaginationChange,
  onRefreshTable,
  rowCount,
  isLoading,
  tableFilters,
  onTableFiltersChange,
  /** Override toolbar tabs (e.g. Approved / Rejected for reports). */
  toolbarTabItems,
  /** When true, assessment modal is view-only (no save/submit). */
  assessmentForceReadOnly = false,
  sorting,
  onSortingChange,
}) {
  const [selectedTask, setSelectedTask] = useState(null);
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [pendingOpenNextFromNextPage, setPendingOpenNextFromNextPage] =
    useState(false);
  const [localColumnFilters, setLocalColumnFilters] = useState([]);
  const [localGlobalFilter, setLocalGlobalFilter] = useState('');

  const theme = useTheme();
  /** Phones / small tablets: prefer popover filters and compact pagination chrome. */
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  /** Small phones: trim pagination extras. */
  const isBelowSm = useMediaQuery(theme.breakpoints.down('sm'));

  const tabItems = toolbarTabItems ?? DEFAULT_TOOLBAR_TAB_ITEMS;

  /** Status tabs live in the table toolbar to match the legacy QC app layout. */
  const renderTopToolbarCustomActions = useCallback(
    () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: { xs: '100%', md: 'auto' },
          minWidth: 0,
          mr: { xs: 0, md: 1 },
          mb: { xs: 0.25, md: 0 },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => onTabChange?.(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            width: { xs: '100%', md: 'auto' },
            minHeight: 'unset',
            '& .MuiTabs-scrollButtons': {
              width: { xs: 28, sm: 32 },
              '&.Mui-disabled': { opacity: 0.35 },
            },
            '& .MuiTabs-flexContainer': {
              gap: { xs: 0, sm: 0.5 },
            },
            '& .MuiTab-root': {
              minHeight: { xs: 34, sm: 38, md: 36 },
              py: { xs: 0.35, sm: 0.5, md: 0 },
              px: { xs: 0.75, sm: 1 },
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
              textTransform: 'none',
            },
          }}
        >
          {tabItems.map(({ value, label }) => (
            <Tab
              key={value}
              value={value}
              label={label}
              sx={
                String(label).length > 12 ? { whiteSpace: 'nowrap' } : undefined
              }
            />
          ))}
        </Tabs>
      </Box>
    ),
    [activeTab, onTabChange, tabItems],
  );

  const handleOpenTask = useCallback((task) => {
    setSelectedTask(task ?? null);
    setIsAssessmentModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsAssessmentModalOpen(false);
    if (typeof onRefreshTable === 'function') {
      onRefreshTable();
    }
  }, [onRefreshTable]);

  const handleMoveToNextTask = useCallback(
    (currentTask) => {
      if (!Array.isArray(data) || data.length === 0) {
        setIsAssessmentModalOpen(false);
        return;
      }
      const currentId = String(
        currentTask?.assignment_id ?? currentTask?.assignmentId ?? '',
      );
      const idx = data.findIndex(
        (row) =>
          String(row?.assignment_id ?? row?.assignmentId ?? '') === currentId,
      );
      const next = idx >= 0 ? data[idx + 1] : null;
      if (next) {
        setSelectedTask(next);
        setIsAssessmentModalOpen(true);
      } else {
        const pageIndex = Number(pagination?.pageIndex ?? 0);
        const pageSize = Number(pagination?.pageSize ?? defaultPageSize ?? 20);
        const nextPageStart = (pageIndex + 1) * pageSize;
        const hasMorePages = nextPageStart < Number(rowCount ?? 0);
        if (hasMorePages && typeof onPaginationChange === 'function') {
          setPendingOpenNextFromNextPage(true);
          onPaginationChange((prev) => ({
            ...prev,
            pageIndex: Number(prev?.pageIndex ?? 0) + 1,
          }));
        } else {
          setIsAssessmentModalOpen(false);
        }
      }
    },
    [data, pagination, defaultPageSize, rowCount, onPaginationChange],
  );

  useEffect(() => {
    if (!pendingOpenNextFromNextPage) return;
    if (Array.isArray(data) && data.length > 0) {
      setSelectedTask(data[0]);
      setIsAssessmentModalOpen(true);
      setPendingOpenNextFromNextPage(false);
      return;
    }
    if (!isLoading) {
      setPendingOpenNextFromNextPage(false);
      setIsAssessmentModalOpen(false);
    }
  }, [pendingOpenNextFromNextPage, data, isLoading]);

  const columns = useMemo(
    () => getQcTaskTableColumns(handleOpenTask),
    [handleOpenTask],
  );

  const mrtColumnFilters = useMemo(() => {
    return Object.entries(tableFilters ?? {})
      // `search` maps to MRT's separate global-filter concept, not a per-column filter.
      .filter(([id]) => id !== 'search')
      .map(([id, value]) => ({ id, value }))
      .filter(({ value }) => {
        const s = value == null ? '' : String(value).trim();
        return s !== '';
      })
      .map(({ id, value }) => ({
        id,
        value: String(value),
      }));
  }, [tableFilters]);

  // Keep the table UI responsive: update local filters immediately,
  // but debounce pushing them to URL/API for server-side filtering.
  useEffect(() => {
    setLocalColumnFilters(mrtColumnFilters);
  }, [mrtColumnFilters]);

  useEffect(() => {
    setLocalGlobalFilter(tableFilters?.search ?? '');
  }, [tableFilters]);

  const filterDebounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (filterDebounceTimerRef.current) {
        clearTimeout(filterDebounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Shared by onColumnFiltersChange / onGlobalFilterChange: builds the combined filter
   * object (column filters + search) and pushes it to the URL/API, debounced. Both column
   * filters and the search box write here so neither one clobbers the other's active value.
   */
  const pushFilters = useCallback(
    (columnFiltersList, searchValue) => {
      const nextFilters = {};
      (columnFiltersList ?? []).forEach((f) => {
        if (!f?.id) return;
        const v = f?.value;
        const s = v == null ? '' : String(v).trim();
        if (s) nextFilters[f.id] = s;
      });
      const search = searchValue == null ? '' : String(searchValue).trim();
      if (search) nextFilters.search = search;

      // Keep table pagination consistent with server results immediately.
      if (typeof onPaginationChange === 'function') {
        onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
      }

      // Debounce URL updates (and therefore API calls).
      if (filterDebounceTimerRef.current) {
        clearTimeout(filterDebounceTimerRef.current);
      }
      filterDebounceTimerRef.current = setTimeout(() => {
        try {
          onTableFiltersChange?.(nextFilters);
        } catch (err) {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn('[QcTaskTable] onTableFiltersChange threw', err);
          }
        }
      }, 400);
    },
    [onPaginationChange, onTableFiltersChange],
  );

  const tableOptions = useMemo(
    () => ({
      columns,
      data,
      enableRowVirtualization: true,
      enableStickyHeader: true,
      enableColumnActions: true,
      enableGlobalFilter: true,
      enableColumnFilters: true,
      enableFilters: true,
      // Popover on mobile/tablet avoids a tall filter row; subheader on desktop.
      columnFilterDisplayMode: isBelowMd ? 'popover' : 'subheader',
      enableHiding: true,
      enableSorting: true,
      enableColumnOrdering: true,
      enableColumnResizing: true,
      enableDensityToggle: true,
      enableFullScreenToggle: true,
      enablePagination: true,
      layoutMode: 'grid',
      memoMode: 'cells',
      enableColumnPinning: true,
      manualPagination: true,
      manualFiltering: true,
      manualSorting: true,
      enableClickToCopy: true,
      rowCount: rowCount ?? 0,
      state: {
        isLoading: Boolean(isLoading),
        pagination: pagination ?? {
          pageIndex: 0,
          pageSize: defaultPageSize ?? 20,
        },
        columnFilters: localColumnFilters,
        globalFilter: localGlobalFilter,
        sorting: sorting ?? [],
      },
      onPaginationChange,
      onSortingChange,
      onColumnFiltersChange: (updater) => {
        const next =
          typeof updater === 'function' ? updater(localColumnFilters) : updater;

        setLocalColumnFilters(next ?? []);
        pushFilters(next, localGlobalFilter);
      },
      onGlobalFilterChange: (updater) => {
        const next =
          typeof updater === 'function' ? updater(localGlobalFilter) : updater;
        const s = next == null ? '' : String(next);

        setLocalGlobalFilter(s);
        pushFilters(localColumnFilters, s);
      },
      initialState: {
        density: 'compact',
        pagination: {
          pageIndex: 0,
          pageSize: defaultPageSize ?? 20,
        },
        columnPinning: {
          left: ['serial', 'taskAction'],
        },
      },
      muiTableContainerProps: {
        sx: {
          width: '100%',
          // Slightly shorter chrome on phones; dvh tracks visible viewport.
          height: {
            xs: 'min(max(220px, 44dvh), 460px)',
            sm: 'min(max(280px, 48dvh), 560px)',
            md: 720,
            lg: 780,
            xl: 820,
          },
          maxHeight: {
            xs: 'min(max(220px, 44dvh), 460px)',
            sm: 'min(max(280px, 48dvh), 560px)',
            md: 720,
            lg: 780,
            xl: 820,
          },
          overflowY: 'auto',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          // Prefer horizontal pan on touch so thead + body scroll together on phones
          touchAction: { xs: 'pan-x pan-y', md: 'auto' },
          overscrollBehaviorX: { xs: 'contain', md: 'auto' },
        },
      },
      muiTablePaperProps: ({ table }) => ({
        sx: {
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          borderRadius: { xs: 1, sm: 1.25, md: 2 },
          bgcolor: 'background.paper',
        },
        // MRT merges this after its fullscreen styles; keep high z-index only when fullscreen
        // so the mobile drawer (z-50) and backdrop stay above the table.
        style: table.getState().isFullScreen ? { zIndex: 1_000_000 } : {},
      }),
      muiTableProps: {
        sx: {
          minWidth: 'max-content',
        },
      },
      /** Tighter cells on small viewports for a compact mobile table. */
      muiTableHeadCellProps: {
        sx: {
          fontSize: { xs: '0.625rem', sm: '0.6875rem', md: '0.75rem' },
          fontWeight: 600,
          py: { xs: 0.35, sm: 0.45, md: 0.6 },
          px: { xs: 0.35, sm: 0.5, md: 0.75 },
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' },
          py: { xs: 0.3, sm: 0.4, md: 0.5 },
          px: { xs: 0.35, sm: 0.5, md: 0.75 },
          lineHeight: 1.25,
          verticalAlign: 'middle',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
      positionGlobalFilter: isBelowMd ? 'left' : 'right',
      muiSearchTextFieldProps: {
        placeholder: 'Search…',
        size: 'small',
        sx: {
          flex: { xs: '1 1 100%', sm: '1 1 auto' },
          minWidth: { xs: 0, sm: 200 },
          maxWidth: { xs: '100%', sm: 360 },
          '& .MuiOutlinedInput-root': {
            minHeight: { xs: 34, sm: 36, md: 40 },
            fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
          },
        },
      },
      muiTopToolbarProps: {
        sx: {
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 0.5, sm: 0.75, md: 0.5 },
          py: { xs: 0.5, sm: 0.65, md: 0.5 },
          px: { xs: 0.25, sm: 0.5, md: 0 },
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          maxWidth: '100%',
          // Scroll the whole toolbar row sideways on small screens (search + tabs + icons)
          overflowX: { xs: 'auto', md: 'hidden' },
          overflowY: { xs: 'visible', md: 'hidden' },
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: { xs: 'thin', md: undefined },
          '& .MuiIconButton-root': {
            padding: { xs: 0.35, sm: 0.5, md: 0.65 },
          },
        },
      },
      muiBottomToolbarProps: {
        sx: {
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-end' },
          gap: { xs: 0.5, sm: 0.5 },
          py: { xs: 0.5, sm: 0.65 },
          px: { xs: 0.25, sm: 1 },
          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
        },
      },
      renderTopToolbarCustomActions,
      muiPaginationProps: {
        rowsPerPageOptions: pageSizeOptions ?? [10, 20, 60],
        showFirstButton: !isBelowSm,
        showLastButton: !isBelowSm,
        SelectProps: {
          MenuProps: {
            disableScrollLock: true,
            PaperProps: {
              sx: {
                maxHeight: { xs: '45dvh', sm: '50vh' },
                overflowY: 'auto',
              },
            },
          },
        },
      },
    }),
    [
      columns,
      data,
      defaultPageSize,
      isBelowMd,
      isBelowSm,
      isLoading,
      onPaginationChange,
      pageSizeOptions,
      pagination,
      renderTopToolbarCustomActions,
      rowCount,
      localColumnFilters,
      localGlobalFilter,
      pushFilters,
      sorting,
      onSortingChange,
    ],
  );

  const table = useMaterialReactTable(tableOptions);

  return (
    <>
      <MaterialReactTable table={table} />
      <QcAssessmentModal
        open={isAssessmentModalOpen}
        onClose={handleCloseModal}
        task={selectedTask}
        pageWorkflowId={pageWorkflowId}
        onMoveToNextTask={handleMoveToNextTask}
        forceReadOnly={assessmentForceReadOnly}
      />
    </>
  );
}

const QcTaskTable = memo(QcTaskTableComponent);
export default QcTaskTable;
